// Single source of truth for the terms text, shared by the Terms & Conditions
// page and the acceptance popup shown to first-time visitors.
import "./TermsContent.css";

export const TERMS_LAST_UPDATED = "19 August 2026";

export const termsSections = [
  {
    id: "acceptance",
    title: "Acceptance of these Terms",
    body: (
      <>
        <p>
          This website is owned and operated by Anushthan Idea Labs ("Anushthan
          Idea Labs", "we", "us" or "our"). By accessing this website,
          submitting an enquiry through our contact form, requesting a proposal,
          or engaging us for any service, you ("you", "the Client") confirm that
          you have read, understood and agreed to be bound by these Terms and
          Conditions ("Terms").
        </p>
        <p>
          If you do not agree with any part of these Terms, please do not use
          this website or engage our services. If you are accepting these Terms
          on behalf of a company, organisation, campaign or other legal entity,
          you confirm that you are authorised to bind that entity.
        </p>
      </>
    ),
  },
  {
    id: "definitions",
    title: "Definitions",
    body: (
      <ul>
        <li>
          <strong>"Services"</strong> means any service we provide, including
          development, design, digital marketing, political consultation,
          animation (2D / 3D / CGI), automation, game development, public
          relations, AR/VR solutions and offline activities.
        </li>
        <li>
          <strong>"Deliverables"</strong> or <strong>"Work"</strong> means all
          materials produced by us for the Client, including websites,
          applications, source code, designs, artwork, animations, creatives,
          copy, campaign assets, strategy documents, reports, builds, renders,
          project files and back-ups.
        </li>
        <li>
          <strong>"Engagement"</strong> means the specific project or retainer
          agreed between us, as described in the accepted proposal, quotation,
          statement of work, purchase order or written confirmation.
        </li>
        <li>
          <strong>"Work Completion"</strong> means the date on which the
          Deliverables (or the relevant milestone or phase) are first made
          available, shared, presented, delivered or deployed to the Client,
          whether or not the Client has formally signed off on them.
        </li>
      </ul>
    ),
  },
  {
    id: "services",
    title: "Our Services",
    body: (
      <>
        <p>
          We are a full-service marketing and idea lab offering development,
          design, digital marketing, political consultation, animation,
          automation, game development, public relations, AR/VR solutions and
          offline activities. The exact scope, deliverables, timelines and
          commercial terms of each Engagement are set out in the proposal,
          quotation or statement of work agreed for that Engagement.
        </p>
        <p>
          Anything not expressly included in the agreed scope is out of scope and
          will be quoted separately. We may engage freelancers, partner studios,
          vendors or subcontractors to deliver parts of an Engagement, and we
          remain responsible to the Client for the work they perform on our
          behalf.
        </p>
      </>
    ),
  },
  {
    id: "engagements",
    title: "Proposals, Quotations & Engagements",
    body: (
      <>
        <p>
          Quotations and proposals are valid for thirty (30) days from the date
          of issue unless stated otherwise, and are based on the information
          supplied by the Client at the time. If that information changes
          materially, we may revise the quotation.
        </p>
        <p>
          An Engagement begins when the Client accepts a proposal in writing
          (including by email or messaging), issues a purchase order, or pays the
          booking or advance amount. Where there is a signed master services
          agreement or statement of work between us, that document will prevail
          over these Terms to the extent of any conflict; in all other respects
          these Terms apply.
        </p>
      </>
    ),
  },
  {
    id: "client-responsibilities",
    title: "Client Responsibilities",
    body: (
      <>
        <p>The Client agrees to:</p>
        <ul>
          <li>
            provide accurate briefs, brand assets, content, logins, product
            information and approvals promptly and in usable formats;
          </li>
          <li>
            nominate a single point of contact authorised to give feedback and
            approvals on the Client's behalf;
          </li>
          <li>
            ensure that all material supplied to us is accurate, lawful, and
            either owned by the Client or properly licensed for the intended use;
          </li>
          <li>
            ensure that any political, public relations, promotional or
            claims-based content complies with all applicable laws, advertising
            codes, platform policies and, where relevant, election and campaign
            regulations;
          </li>
          <li>
            review Deliverables and provide consolidated feedback within the
            timeframes agreed for the Engagement; and
          </li>
          <li>make payments in accordance with the agreed payment schedule.</li>
        </ul>
        <p>
          We are not liable for delays, additional costs, rejected campaigns,
          takedowns or losses caused by incomplete, late, inaccurate or unlawful
          material supplied by the Client.
        </p>
      </>
    ),
  },
  {
    id: "fees",
    title: "Fees, Invoicing & Payment",
    body: (
      <>
        <p>
          Fees are as set out in the accepted proposal or quotation. Unless
          agreed otherwise in writing:
        </p>
        <ul>
          <li>
            an advance or booking amount is payable before work commences, and
            the balance is payable as per the agreed milestones or on Work
            Completion;
          </li>
          <li>retainer fees are invoiced in advance for each billing cycle;</li>
          <li>
            invoices are payable within the period stated on the invoice and,
            where no period is stated, within fifteen (15) days of the invoice
            date;
          </li>
          <li>
            all fees are exclusive of applicable taxes, duties and levies
            (including GST), which will be charged additionally at the prevailing
            rates;
          </li>
          <li>
            third-party costs such as media and ad spends, licences, stock
            assets, fonts, plugins, hosting, domains, printing, venue, production
            and travel are billed at actual and are payable in advance unless
            agreed otherwise; and
          </li>
          <li>
            bank charges, payment gateway fees and currency conversion charges
            are borne by the Client.
          </li>
        </ul>
        <p>
          Fees already paid are non-refundable once work has commenced, as they
          cover time, resources and capacity already committed to the Engagement.
          We may charge interest on overdue amounts at 1.5% per month (or the
          maximum permitted by law, if lower) from the due date until payment is
          received, and may recover reasonable costs of collection.
        </p>
        <p>
          We may suspend work, withhold Deliverables, pause campaigns, and revoke
          access to files, accounts, staging environments or dashboards while any
          invoice remains overdue, and where an invoice remains unpaid after two
          (2) written payment reminders or for more than sixty (60) days from
          Work Completion, the Deliverables may be removed and permanently
          deleted on the terms set out in the clause headed "Term, Suspension &
          Termination" below.
        </p>
      </>
    ),
  },
  {
    id: "revisions",
    title: "Revisions & Change Requests",
    body: (
      <>
        <p>
          Each Engagement includes the number of revision rounds specified in the
          proposal. Additional rounds, changes to an approved direction, new
          requirements, or expansions of scope will be quoted and billed
          separately, and may affect agreed timelines.
        </p>
        <p>
          Deliverables are deemed approved if the Client does not raise written
          feedback within seven (7) days of them being shared, or if the Client
          publishes, deploys or otherwise puts them to commercial use.
        </p>
      </>
    ),
  },
  {
    id: "timelines",
    title: "Timelines & Delays",
    body: (
      <p>
        Timelines shared in a proposal are estimates based on timely inputs,
        feedback and payments from the Client. Delays caused by the Client, by
        third parties, or by changes in scope will extend delivery dates
        accordingly. Where an Engagement is put on hold by the Client for more
        than thirty (30) days, we may re-plan it subject to availability and
        revise the commercials.
      </p>
    ),
  },
  {
    id: "ip",
    title: "Intellectual Property & Ownership",
    body: (
      <>
        <p>
          All intellectual property rights in the Deliverables remain the
          exclusive property of Anushthan Idea Labs until we have received
          payment in full for the relevant Engagement. On receipt of full
          payment, ownership of the final approved Deliverables created
          specifically for the Client transfers to the Client, subject to the
          exceptions below.
        </p>
        <p>The following do not transfer and remain our property:</p>
        <ul>
          <li>
            our pre-existing tools, frameworks, code libraries, rigs, templates,
            methodologies, know-how and internal systems, for which the Client
            receives a non-exclusive, non-transferable licence to use as part of
            the Deliverables;
          </li>
          <li>
            concepts, drafts, routes and options that were presented but not
            selected and paid for; and
          </li>
          <li>
            third-party materials such as stock assets, fonts, plugins, music and
            software, which remain subject to their own licences and which the
            Client is responsible for maintaining.
          </li>
        </ul>
        <p>
          The Client warrants that any material it supplies to us does not
          infringe the intellectual property or other rights of any third party.
        </p>
      </>
    ),
  },
  {
    id: "portfolio",
    title: "Portfolio & Promotional Rights",
    body: (
      <p>
        Unless the Client tells us otherwise in writing, we may display the Work,
        the Client's name and logo, and non-confidential campaign results in our
        portfolio, case studies, blogs, website, social media channels, award
        entries and business presentations. Confidential commercial information
        will not be disclosed without the Client's consent. A Client may request
        removal of such material at any time by writing to us, and we will action
        reasonable requests within a reasonable period.
      </p>
    ),
  },
  {
    id: "third-party",
    title: "Third-Party Platforms & Ad Spends",
    body: (
      <>
        <p>
          Many of our Services depend on third-party platforms such as search
          engines, social networks, ad networks, hosting providers, app stores,
          analytics tools and payment gateways. These platforms are controlled by
          third parties and their policies, algorithms, pricing, approval
          processes and availability may change without notice.
        </p>
        <p>
          We are not responsible for account suspensions, ad disapprovals,
          content takedowns, ranking changes, outages, data loss or price
          increases caused by such platforms. Media and ad budgets are the
          Client's spends; where we manage them, funds must be provided in
          advance and are used strictly as per the approved media plan.
        </p>
      </>
    ),
  },
  {
    id: "confidentiality",
    title: "Confidentiality",
    body: (
      <p>
        Each party agrees to keep confidential all non-public information
        received from the other in connection with an Engagement, to use it only
        for the purposes of that Engagement, and not to disclose it to third
        parties except to employees, partners and subcontractors who need it and
        are bound by equivalent obligations. This obligation does not apply to
        information that is publicly available, was already lawfully known, or is
        required to be disclosed by law.
      </p>
    ),
  },
  {
    id: "website-use",
    title: "Use of this Website",
    body: (
      <>
        <p>
          All content on this website, including text, graphics, logos, images,
          videos, blogs, case studies and design, is owned by or licensed to
          Anushthan Idea Labs and is protected by applicable intellectual
          property laws. You may view and share our content for personal,
          non-commercial purposes with attribution, but you may not copy,
          reproduce, republish, sell or exploit it commercially without our prior
          written permission.
        </p>
        <p>You agree not to:</p>
        <ul>
          <li>use this website for any unlawful, misleading or harmful purpose;</li>
          <li>
            attempt to gain unauthorised access to any part of the website, its
            systems or its data;
          </li>
          <li>introduce viruses, malicious code or automated scraping tools; or</li>
          <li>
            misuse our forms, contact details or communication channels for spam
            or bulk solicitation.
          </li>
        </ul>
        <p>
          Our website may contain links to third-party sites, including our
          social media pages and articles published on external platforms. We do
          not control and are not responsible for the content or practices of
          those sites.
        </p>
      </>
    ),
  },
  {
    id: "submissions",
    title: "Enquiries & Information You Send Us",
    body: (
      <>
        <p>
          When you submit our contact form or a career enquiry, you provide us
          with information such as your name, email address, phone number,
          message and any file you choose to attach. We collect and store this
          information solely to respond to your enquiry, evaluate a potential
          engagement or application, and maintain our business records, and we
          process it using reputable third-party service providers.
        </p>
        <p>
          Please do not submit sensitive, confidential or third-party personal
          information through our forms. You confirm that the information you
          submit is accurate and that you are entitled to share it. We do not
          sell your information. Unsolicited ideas, briefs or concepts sent to us
          are shared at your own risk and create no obligation of confidentiality
          or compensation unless we have agreed otherwise in writing.
        </p>
      </>
    ),
  },
  {
    id: "no-guarantee",
    title: "No Guarantee of Results",
    body: (
      <>
        <p>
          Marketing, public relations, political consulting and campaign outcomes
          depend on many factors outside our control, including market
          conditions, competition, audience behaviour, platform algorithms, media
          environment, budgets and the Client's own product, pricing and internal
          execution.
        </p>
        <p>
          While we apply professional skill, experience and care to every
          Engagement, we do not guarantee any specific reach, ranking,
          engagement, leads, conversions, sales, coverage, sentiment, election or
          business outcome. Any projections, benchmarks or estimates we share are
          indicative only and do not constitute a warranty.
        </p>
      </>
    ),
  },
  {
    id: "warranties",
    title: "Disclaimers & Warranties",
    body: (
      <>
        <p>
          We warrant that the Services will be performed with reasonable skill
          and care by suitably experienced personnel. Except as expressly stated
          in these Terms, this website and all Services and Deliverables are
          provided on an "as is" and "as available" basis, and all other
          warranties, whether express or implied, including implied warranties of
          merchantability, fitness for a particular purpose and
          non-infringement, are excluded to the fullest extent permitted by law.
        </p>
        <p>
          Content on this website, including blogs and case studies, is provided
          for general information only and does not constitute professional,
          legal, financial or political advice.
        </p>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    body: (
      <>
        <p>
          To the maximum extent permitted by law, Anushthan Idea Labs, its
          directors, employees, partners and subcontractors shall not be liable
          for any indirect, incidental, special, punitive or consequential loss,
          or for any loss of profits, revenue, goodwill, business opportunity,
          data or anticipated savings, arising out of or in connection with this
          website, the Services or these Terms, even if advised of the
          possibility of such loss.
        </p>
        <p>
          Our total aggregate liability in connection with any Engagement shall
          not exceed the total fees actually paid by the Client to us for that
          Engagement in the three (3) months immediately preceding the event
          giving rise to the claim. Nothing in these Terms excludes liability
          that cannot be excluded under applicable law.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnity",
    body: (
      <p>
        The Client agrees to indemnify and hold harmless Anushthan Idea Labs
        against all claims, demands, penalties, losses, damages and reasonable
        legal costs arising from the Client's breach of these Terms, from
        material supplied by the Client, from the Client's use or publication of
        the Deliverables, or from any claim that such material or use infringes
        third-party rights or violates applicable law, advertising codes or
        platform policies.
      </p>
    ),
  },
  {
    id: "termination",
    title: "Term, Suspension & Termination",
    body: (
      <>
        <p>
          Either party may terminate an Engagement by giving thirty (30) days'
          written notice, unless a different notice period is agreed for that
          Engagement. We may terminate or suspend an Engagement immediately if
          the Client fails to pay amounts when due, breaches these Terms, or
          requires work that is unlawful, misleading or contrary to professional
          or platform standards.
        </p>
        <p>
          On termination, the Client shall pay for all work performed and
          third-party costs committed up to the termination date, and shall
          return or destroy any of our confidential material in its possession.
          Access to shared drives, project boards, staging environments,
          dashboards and reporting tools may be withdrawn on or after the
          termination date, and any licences granted under an Engagement are
          conditional on the Client having met its payment obligations in
          respect of that Engagement.
        </p>
        <p>
          Where an invoice remains unpaid after two (2) written payment reminders
          have been issued to the Client, or where payment is not received within
          sixty (60) days of Work Completion, whichever occurs earlier, we
          reserve the right, at our sole discretion and without further notice or
          liability, to withhold, unpublish, take down, deactivate, recall and
          permanently delete any and all Work and Deliverables produced for the
          Client under the relevant Engagement, including all associated source
          files, project files, creatives, campaign assets, builds, hosting
          environments, accounts and back-ups in our possession or control.
          Written reminders may be sent by email to the Client's registered or
          last-used email address and shall be deemed received on the date of
          sending; no licence, assignment or ownership right in the Work shall
          pass to the Client until all amounts due have been received in full;
          the Client shall have no claim against us for any loss, damage,
          business interruption or expense arising out of such withholding,
          takedown or deletion; any amounts already paid shall remain
          non-refundable; and the Client shall continue to remain liable for all
          outstanding sums, together with applicable interest and costs of
          recovery. Once Deliverables have been deleted, recovery may be
          impossible, and any request to recreate them will be treated as a fresh
          Engagement and quoted separately. Clients are advised to maintain their
          own copies and back-ups of all Deliverables released to them.
        </p>
        <p>
          Clauses relating to payment, intellectual property, confidentiality,
          disclaimers, limitation of liability, indemnity and governing law
          survive termination.
        </p>
      </>
    ),
  },
  {
    id: "force-majeure",
    title: "Force Majeure",
    body: (
      <p>
        Neither party shall be liable for any failure or delay in performing its
        obligations (other than payment obligations) caused by events beyond its
        reasonable control, including natural disasters, epidemics, war, civil
        unrest, strikes, government action, changes in law, internet or power
        failures, cyber attacks, or the failure of third-party platforms and
        service providers.
      </p>
    ),
  },
  {
    id: "governing-law",
    title: "Governing Law & Dispute Resolution",
    body: (
      <p>
        These Terms and any Engagement are governed by the laws of India. The
        parties will first attempt to resolve any dispute amicably through
        discussion between senior representatives. If a dispute is not resolved
        within thirty (30) days, it shall be subject to the exclusive
        jurisdiction of the competent courts in India having jurisdiction over
        the registered office of Anushthan Idea Labs.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to these Terms",
    body: (
      <p>
        We may update these Terms from time to time to reflect changes in our
        services, business practices or legal requirements. The revised version
        takes effect from the date it is published, and the "Last updated" date
        shown above will be amended accordingly. Continued use of this
        website or of our Services after publication constitutes acceptance of
        the revised Terms. For ongoing Engagements, the Terms in force at the
        time the Engagement was accepted will continue to apply unless we agree
        otherwise in writing.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact Us",
    body: (
      <>
        <p>
          If you have any questions about these Terms and Conditions, or about an
          invoice, an Engagement or the removal of your data, please get in
          touch:
        </p>
        <div className="terms-contact-card">
          <p>
            <strong>Anushthan Idea Labs</strong>
          </p>
          <p>
            Email:{" "}
            <a href="mailto:anushthanidealabs@gmail.com">
              anushthanidealabs@gmail.com
            </a>
          </p>
          <p className="mb-0">
            Or write to us through our <a href="/contact-us">contact form</a>.
          </p>
        </div>
      </>
    ),
  },
];
